import React, { useMemo } from "react";
import BulkUpdateFieldsModal from "./entity/BulkUpdateFieldsModal";

export default function BulkUpdateEventsModal({
  open,
  onClose,
  selectedCount = 0,
  programs = [],
  programOrganizers = [],
  loading = false,
  onApply,
}) {
  const programOptions = useMemo(() => {
    const list = Array.isArray(programs) ? programs : [];
    return [...list]
      .filter((p) => p && p._id)
      .map((p) => ({ id: String(p._id), name: String(p.name || "").trim() }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [programs]);

  const programNameById = useMemo(
    () => Object.fromEntries(programOptions.map((p) => [p.id, p.name])),
    [programOptions]
  );

  const organizerOptions = useMemo(() => {
    const list = Array.isArray(programOrganizers) ? programOrganizers : [];
    return [...list]
      .filter((lead) => lead && lead._id)
      .map((lead) => {
        const programName = programNameById[String(lead.program_id || "")] || "Program";
        const person = [lead.first_name, lead.last_name].filter(Boolean).join(" ").trim();
        const label = person ? `${programName} — ${person}` : `${programName} — ${String(lead.email || lead._id)}`;
        return { value: String(lead._id), label };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [programOrganizers, programNameById]);

  return (
    <BulkUpdateFieldsModal
      open={open}
      onClose={loading ? undefined : onClose}
      title={`Bulk update (${selectedCount || 0})`}
      subtitle="Choose values to apply to all selected events. When changing Program, also set Event lead."
      loading={loading}
      applyLabel="Apply update"
      selectedCount={selectedCount}
      onApply={(payload) => {
        if (!onApply) return;
        const next = {};
        if (Object.prototype.hasOwnProperty.call(payload, "program_id")) {
          next.program_id = String(payload.program_id || "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "event_organizer_id")) {
          next.event_organizer_id = String(payload.event_organizer_id || "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "location")) {
          next.location = String(payload.location || "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "visibility")) {
          next.visibility = String(payload.visibility || "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "audience_type")) {
          next.audience_type = String(payload.audience_type || "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "max_capacity")) {
          next.max_capacity = String(payload.max_capacity ?? "").trim();
        }
        if (Object.prototype.hasOwnProperty.call(payload, "event_date")) {
          next.event_date = String(payload.event_date || "").trim();
        }
        onApply(next);
      }}
      fields={[
        {
          id: "event_date",
          label: "Event date",
          type: "date",
          helpText: "Sets the calendar date on each selected event. Start and end times are preserved.",
        },
        {
          id: "program_id",
          label: "Program",
          type: "select",
          options: programOptions.map((p) => ({ value: p.id, label: p.name || p.id })),
          allowEmpty: false,
        },
        {
          id: "event_organizer_id",
          label: "Event lead",
          type: "select",
          options: organizerOptions,
          allowEmpty: false,
        },
        {
          id: "location",
          label: "Location",
          type: "text",
          placeholder: "Enter location (blank to clear)",
        },
        {
          id: "visibility",
          label: "Visibility",
          type: "select",
          options: [
            { value: "public", label: "Public" },
            { value: "private", label: "Private" },
          ],
          allowEmpty: false,
        },
        {
          id: "audience_type",
          label: "Audience type",
          type: "select",
          options: [
            { value: "Youth Only", label: "Youth Only" },
            { value: "Adult Only", label: "Adult Only" },
            { value: "All Ages", label: "All Ages" },
          ],
          allowEmpty: false,
        },
        {
          id: "max_capacity",
          label: "Max capacity",
          type: "text",
          placeholder: "Enter capacity (blank to clear)",
          helpText: "Blank clears the capacity limit.",
        },
      ]}
    />
  );
}
